import { useAuth } from "@/_core/hooks/useAuth";
import { FaqList } from "@/components/FaqList";
import { SitePageShell } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { INQUIRY_TYPE_LABEL } from "@shared/inquiry";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useLocation } from "wouter";
import type { InquiryType } from "../../../drizzle/schema";

export default function InquiryNewPage() {
  const { isAuthenticated, loading } = useAuth();
  const [, navigate] = useLocation();
  const orderId = Number(new URLSearchParams(window.location.search).get("orderId") ?? 0);

  const orders = trpc.orders.listMine.useQuery(undefined, { enabled: isAuthenticated });
  const utils = trpc.useUtils();
  const create = trpc.inquiries.create.useMutation({
    onSuccess: () => {
      utils.inquiries.listMine.invalidate();
      toast.success("문의가 접수되었습니다.");
      navigate("/my");
    },
    onError: (e) => toast.error(e.message),
  });

  const [type, setType] = useState<InquiryType>("other");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (!loading && !isAuthenticated) window.location.href = getLoginUrl();
  }, [loading, isAuthenticated]);

  if (loading || !isAuthenticated || orders.isLoading) {
    return (
      <SitePageShell>
        <div className="container py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </SitePageShell>
    );
  }

  // Ownership is re-checked on the server; this just resolves the order for display.
  const order = orders.data?.find((o) => o.id === orderId);
  if (!order) {
    return (
      <SitePageShell>
        <div className="container py-20 text-center text-muted-foreground">
          <div className="mb-4">문의할 주문을 찾을 수 없습니다.</div>
          <Button asChild>
            <Link href="/my">마이페이지로</Link>
          </Button>
        </div>
      </SitePageShell>
    );
  }

  const canSubmit = title.trim().length > 0 && content.trim().length > 0 && !create.isPending;

  return (
    <SitePageShell>
      <div className="container py-10 md:py-14 max-w-2xl">
        <h1 className="font-serif text-3xl text-primary mb-1">1:1 문의하기</h1>
        <p className="text-sm text-muted-foreground mb-6">
          먼저 아래 자주 묻는 질문을 확인해 보세요.
        </p>

        <Card className="mb-8">
          <CardContent className="px-5 py-2">
            <FaqList />
          </CardContent>
        </Card>

        <div className="font-serif text-lg text-primary mb-3">해결되지 않았나요?</div>
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="text-sm">
              <span className="text-muted-foreground">주문번호</span>{" "}
              <span className="font-mono">{order.orderNo}</span>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-muted-foreground">문의 유형</Label>
              <Select value={type} onValueChange={(v) => setType(v as InquiryType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(INQUIRY_TYPE_LABEL) as InquiryType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {INQUIRY_TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-muted-foreground">제목</Label>
              <Input
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="문의 제목을 입력해 주세요"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-muted-foreground">내용</Label>
              <Textarea
                value={content}
                maxLength={3000}
                rows={6}
                onChange={(e) => setContent(e.target.value)}
                placeholder="문의 내용을 자세히 적어 주세요"
              />
            </div>
            <div className="flex gap-2">
              <Button
                disabled={!canSubmit}
                onClick={() =>
                  create.mutate({
                    orderId: order.id,
                    type,
                    title: title.trim(),
                    content: content.trim(),
                  })
                }
              >
                {create.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                문의 접수
              </Button>
              <Button asChild variant="outline">
                <Link href="/my">취소</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </SitePageShell>
  );
}
